import { dustUpliftCents } from '../dust-evidence.ts';
/**
 * Quote pricing engine (spec §9).
 *
 * This module is pure and shared: the browser runs it for a live preview and
 * the Worker runs it to produce the authoritative figures that get written to
 * the database. The Worker never trusts totals sent by the browser (spec §9.1).
 *
 * All arithmetic is integer arithmetic. Money is cents, time is minutes and
 * multipliers are basis points (10,000 = 1.00), so there are no floating-point
 * rounding surprises in stored values.
 */

import { BASIS_POINTS_SCALE, FREQUENCY_OCCURRENCES_PER_YEAR, MAX_AMOUNT_CENTS } from '../constants/index.ts';
import { clampGroupQuantity } from '../quotes/groups.ts';
import { DEFAULT_CURRENCY_CODE, minimumChargeCents, roundToCurrencyStep } from '../currency.ts';
import type {
  BillingBasis,
  DiscountType,
  MeasurementBasis,
  SeriesFrequency,
  ServiceFrequency,
} from '../constants/index.ts';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../constants/languages.ts';

// ---------------------------------------------------------------------------
// Rounding helpers
// ---------------------------------------------------------------------------

/**
 * Round half away from zero.
 *
 * `Math.round` rounds half toward +Infinity, which makes -0.5 round to -0 and
 * breaks symmetry for the (rare, but possible) negative amounts produced by
 * credit-style fees. Half away from zero is what an invoice reader expects.
 */
export function roundHalfUp(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value);
}

/**
 * One visit's share of a monthly contract total.
 *
 * The arithmetic is the one the quote already uses — `total × 12 / (52 ×
 * visitsPerWeek)` — so a weekly quote sold at two visits a week still prices
 * each visit at a twenty-sixth of the month. When the schedule names a real
 * pattern (Monday/Wednesday/Friday, every two weeks, the 1st and 3rd Tuesday)
 * that pattern is the divisor instead, and the month still adds back up.
 */
export function perVisitCentsFromMonthlyTotal(
  totalCents: number,
  currencyCode: string,
  cadence: {
    frequency: SeriesFrequency;
    visitsPerWeek?: number | null;
    weekdayCount: number;
    intervalWeeks: number;
    monthlyOccurrences: number;
  },
): number {
  const quoted = Math.max(1, cadence.visitsPerWeek ?? 1);
  let numerator = totalCents * 12;
  let denominator = 52 * quoted;

  if (cadence.frequency === 'weekly' || cadence.frequency === 'biweekly') {
    const defaultInterval = cadence.frequency === 'biweekly' ? 2 : 1;
    const days = Math.max(1, cadence.weekdayCount);
    const interval = Math.max(1, cadence.intervalWeeks);
    if (!(days === 1 && interval === defaultInterval)) {
      numerator = totalCents * 12 * interval;
      denominator = 52 * days;
    }
  } else if (cadence.frequency === 'monthly') {
    const occurrences = Math.max(1, cadence.monthlyOccurrences);
    if (occurrences !== 1) {
      numerator = totalCents;
      denominator = occurrences;
    }
  }

  return roundToCurrencyStep(roundHalfUp(numerator / denominator), currencyCode);
}

/** Round a total up to the next whole billing increment (spec §9.8). */
export function applyBillingIncrement(minutes: number, incrementMinutes: number): number {
  if (!Number.isFinite(incrementMinutes) || incrementMinutes <= 1) return minutes;
  if (minutes <= 0) return 0;
  return Math.ceil(minutes / incrementMinutes) * incrementMinutes;
}

/** Same rounding as `applyBillingIncrement`, on an exact minute count. */
function applyBillingIncrementExact(minutes: bigint, incrementMinutes: number): bigint {
  if (minutes <= 0n) return 0n;
  if (!Number.isFinite(incrementMinutes) || incrementMinutes <= 1) return minutes;
  const step = BigInt(Math.trunc(incrementMinutes));
  if (step <= 1n) return minutes;
  return ((minutes + step - 1n) / step) * step;
}

/**
 * `(minutes × hourly rate) ÷ 60`, half-up, from an exact minute count.
 *
 * A monthly total past `MAX_SAFE_INTEGER` is not a safe JS number. Converting
 * it before this multiply drops a minute, and at a low rate that minute is a
 * cent. The cents themselves still fit, so the clamp happens after the divide.
 */
function laborCentsFromExactMinutes(minutes: bigint, hourlyRateCents: number): number {
  if (minutes <= 0n) return 0;
  const rate = BigInt(Math.max(0, Math.trunc(hourlyRateCents)));
  if (rate === 0n) return 0;
  const product = minutes * rate;
  const scale = 60n;
  const quotient = product / scale;
  const remainder = product % scale;
  const rounded = remainder * 2n >= scale ? quotient + 1n : quotient;
  const cap = BigInt(Number.MAX_SAFE_INTEGER);
  if (rounded > cap) return Number.MAX_SAFE_INTEGER;
  return Number(rounded);
}

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

export interface ServiceCalculationInput {
  /** Minutes configured for this service on this room (spec §9.2). */
  readonly estimatedMinutes: number;
  readonly isSelected: boolean;
  /**
   * Whether this task's share of labor belongs in the taxable subtotal.
   *
   * Optional, and omitting it means taxable — which is what every quote
   * written before per-task tax says, and what the quote-wide
   * `laborIsTaxable` flag alone used to decide for all of them.
   */
  readonly isTaxable?: boolean;
  /**
   * How often this task is done, on a quote priced by the month. Optional,
   * and omitting it means 'every_visit' — the truth about every task written
   * before commercial specs existed, and the only value a per-visit quote
   * ever reads (frequencies contribute nothing under 'per_visit').
   */
  readonly frequency?: ServiceFrequency;
  /**
   * How this task's minutes are arrived at (spec: commercial estimating).
   *
   * Optional, and omitting it means 'time' — somebody decided this job takes
   * twelve minutes and typed twelve, which is what every task written before
   * measured estimating says. Under 'area' or 'fixture' the minutes are
   * *derived* from `measuredQuantity` and `productionRatePerHour` and
   * `estimatedMinutes` is ignored, so a stale stored figure can never
   * disagree with the measurement it came from.
   */
  readonly measurementBasis?: MeasurementBasis;
  /**
   * The quantity measured: square feet under 'area', fixtures under
   * 'fixture'.
   *
   * `null` or absent under 'area' means "the area's own floor area" — an open
   * office is vacuumed, dusted and binned over the same 12,000 square feet,
   * and typing it onto each task would be three chances to mistype it. Under
   * 'fixture' there is nothing to inherit (the room has no fixture count),
   * so an absent quantity is zero fixtures and zero minutes.
   */
  readonly measuredQuantity?: number | null;
  /** Units per hour: square feet per hour, or fixtures per hour. */
  readonly productionRatePerHour?: number | null;
}

export interface RoomCalculationInput {
  /** How many identical rooms this line represents. At least 1. */
  readonly quantity: number;
  readonly sizeMultiplierBasisPoints: number;
  readonly conditionMultiplierBasisPoints: number;
  /** Estimator override. `null` means "use the calculated figure". */
  readonly adjustedMinutes: number | null;
  /**
   * A price for the room, charged instead of its time (spec §9.12).
   *
   * `null` — the ordinary case, and every room that existed before flat
   * pricing — prices the room from its minutes and the hourly rate. When set,
   * the room's minutes are still calculated (the crew needs the checklist and
   * the schedule needs the duration) but they no longer reach the labor
   * subtotal, or the room would be charged twice.
   */
  readonly flatPriceCents?: number | null;
  /**
   * The area's floor area in square feet, inherited by every 'area' task on
   * it that does not carry its own quantity.
   *
   * Per room *instance*, not per line: three private offices of 150 sq ft
   * each is `quantity: 3` with 150 here, and the engine multiplies, exactly
   * as it multiplies minutes.
   */
  readonly measuredSquareFeet?: number | null;
  readonly services: readonly ServiceCalculationInput[];
  /**
   * How many copies of this room's group are being priced.
   *
   * Optional, and omitting it means 1 — every quote written before groups
   * existed, and every room that still sits in the single default group.
   * Applied after the room's own quantity: three bedrooms inside an apartment
   * that is itself times forty is `quantity: 3` here and `groupQuantity: 40`.
   */
  readonly groupQuantity?: number;
}

export interface FeeCalculationInput {
  readonly amountCents: number;
  readonly isTaxable: boolean;
}

export interface QuoteCalculationInput {
  /** An unresolved estimator decision has duration, but no available money estimate. */
  readonly pricingBlocked?: boolean;
  /** A confirmed per-visit area price replaces hourly labor, never adds to it. */
  readonly dustUpliftBasisPoints?: number;
  readonly areaPricing?: { readonly squareFeet: number; readonly rateCents: number } | null;
  readonly rooms: readonly RoomCalculationInput[];
  readonly fees: readonly FeeCalculationInput[];
  readonly hourlyRateCents: number;
  readonly billingIncrementMinutes: number;
  readonly discountType: DiscountType | null;
  /** Cents when `discountType` is 'fixed', basis points when 'percentage'. */
  readonly discountValue: number;
  readonly taxEnabled: boolean;
  readonly taxRateBasisPoints: number;
  readonly laborIsTaxable: boolean;
  /**
   * The quote's own currency, which decides how fine a derived amount is
   * allowed to be. Optional, and omitting it is the two-decimal behavior this
   * engine has always had — see the note on rounding in `calculateQuote`.
   */
  readonly currencyCode?: string;
  /**
   * What the total buys: one visit ('per_visit', the default and every quote
   * before commercial mode) or a month of the specified service ('monthly').
   * Under 'monthly' every money output of this engine is per month — labor
   * from the frequency-weighted minutes, fees read as monthly amounts as
   * typed, discount and tax on the monthly figures.
   */
  readonly billingBasis?: BillingBasis;
  /**
   * How many visits a week the monthly price covers. Read only under
   * 'monthly'; clamped to 1–7; omitting it means one visit a week.
   */
  readonly visitsPerWeek?: number | null;
  /**
   * The frequency tier being priced, when the quote offers its scope at more
   * than one service frequency (spec: frequency pricing tiers).
   *
   * An additional discount, not a replacement: it computes off the same
   * discountable subtotal as the quote's own discount — so two percentages
   * stack additively off the base rather than compounding — and the pair is
   * capped together so a quote can never be discounted below zero. It is
   * reported separately (`frequencyDiscountCents`) because the two answer
   * different questions: the quote's discount is something the estimator
   * granted this customer, the tier's is the price of the frequency itself,
   * and a customer page that folded them into one line could not say "save
   * 15% with weekly service" and mean it.
   *
   * Omitting it — or a null `discountType` — is the quote as built, which is
   * also exactly what the 'one_time' tier ordinarily is.
   */
  readonly frequencyTierDiscount?: {
    readonly discountType: DiscountType | null;
    /** Cents when 'fixed', basis points when 'percentage'. */
    readonly discountValue: number;
  } | null;
}

// ---------------------------------------------------------------------------
// Outputs
// ---------------------------------------------------------------------------

export interface RoomCalculationResult {
  /** Selected service minutes × quantity, before multipliers (spec §9.3). */
  readonly baseMinutes: number;
  /** Base minutes after size and condition multipliers (spec §9.4). */
  readonly calculatedMinutes: number;
  /** How long this room takes — for the schedule and the quote (spec §9.5). */
  readonly billableMinutes: number;
  readonly isManuallyAdjusted: boolean;
  /**
   * The room's flat price after quantity and multipliers, or `null` when the
   * room is priced by time. `null` and `0` are different answers: a room
   * priced at zero is free, a room priced at `null` is charged for its time.
   */
  readonly chargeCents: number | null;
  /**
   * The taxable part of `baseMinutes` — selected minutes belonging to tasks
   * that are taxable, times quantity, before any multiplier.
   *
   * Reported as a whole number beside the whole it came from, rather than as a
   * ready-made proportion, so the division happens exactly once, at the money
   * step. A share quantized here — to whole minutes, or to basis points —
   * carries its rounding error into every cent it is later multiplied by: one
   * taxable minute in six is 0.1667 in basis points rather than 0.16666…, and
   * on a room adjusted to 100,000 minutes at $60/hr that is $3.33 of taxable
   * subtotal out of place.
   *
   * Equal to `baseMinutes` unless a selected task on this room is marked
   * exempt.
   */
  readonly taxableBaseMinutes: number;
  /**
   * The group's quantity this room was priced under. 1 when the caller did
   * not set one. The minutes and charge above stay the room's own — the
   * group multiplies them where the quote is totalled, so a card can show
   * "Bedroom × 3" inside "Apartment × 40" without the 40 baked into the room.
   */
  readonly groupQuantity: number;
}

export interface QuoteCalculationResult {
  readonly dustAdjustmentCents?: number;
  readonly dustAmountBlocked?: boolean;
  readonly pricingBlocked?: boolean;
  readonly rooms: readonly RoomCalculationResult[];
  /** Echo of the input, so a stored result says what its numbers mean. */
  readonly billingBasis: BillingBasis;
  /**
   * How long the ORDINARY visit takes: every room's every-visit work, after
   * multipliers, flat-priced rooms included. On a per-visit quote this equals
   * `estimatedMinutes`; on a monthly quote it is the schedule's answer to
   * "how long is Tuesday's visit" while `estimatedMinutes` holds the month.
   */
  readonly routineVisitMinutes: number;
  /**
   * How long the whole job takes, across every room. Drives the appointment
   * length and the "estimated time" line, so flat-priced rooms count here.
   * Under 'monthly' this is the month's total time instead — the staffing
   * question a monthly price is quoted against.
   */
  readonly estimatedMinutes: number;
  /**
   * The minutes labor is actually charged for: time-priced rooms only,
   * rounded up to the billing increment (spec §9.8). Equal to the rounded
   * `estimatedMinutes` on a quote with no flat-priced room.
   */
  readonly billableMinutes: number;
  readonly laborSubtotalCents: number;
  /** Flat-priced rooms, after their multipliers (spec §9.12). */
  readonly roomChargesCents: number;
  readonly fixedFeesCents: number;
  readonly discountCents: number;
  /**
   * The frequency tier's own reduction, separate from `discountCents` so the
   * customer page can print "Weekly rate" as its own line. Zero on every quote
   * priced without a tier.
   */
  readonly frequencyDiscountCents: number;
  readonly taxableSubtotalCents: number;
  readonly taxCents: number;
  readonly totalCents: number;
}

// ---------------------------------------------------------------------------
// Room-level calculation
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Measured tasks: minutes from a quantity and a production rate
// ---------------------------------------------------------------------------

/**
 * Minutes for a measured task: `quantity ÷ rate × 60`.
 *
 * The trade's own arithmetic, and the reason two estimators bidding the same
 * building land within a few dollars of each other. 12,000 square feet at
 * 3,000 square feet an hour is four hours; twelve fixtures at thirty fixtures
 * an hour is twenty-four minutes.
 *
 * Rounded half up to whole minutes, because that is the unit every consumer
 * downstream already speaks: the schedule books minutes, the work order
 * prints minutes, the estimator's override field types minutes. Rounding
 * here rather than at the money step costs at most half a minute a line and
 * buys a stored figure that means exactly what the printed one says.
 *
 * A rate of zero, or a missing one, returns zero — not an infinity. A rate is
 * not "instant work", it is a missing answer, and a missing answer has no
 * business becoming a price.
 */
export function measuredTaskMinutes(input: {
  readonly measuredQuantity: number | null | undefined;
  readonly productionRatePerHour: number | null | undefined;
}): number {
  const quantity = Math.max(0, Math.trunc(input.measuredQuantity ?? 0));
  const rate = Math.trunc(input.productionRatePerHour ?? 0);
  if (quantity === 0 || !Number.isFinite(rate) || rate <= 0) return 0;
  return roundHalfUp((quantity * 60) / rate);
}

/**
 * What one instance of this task actually takes, whichever way it was
 * arrived at — the single answer both the per-visit and the monthly basis
 * ask for, so the two can never derive it differently.
 *
 * Exported because the Worker stores this figure on the task row as well as
 * pricing from it: the work order, the printable checklist, the PDF and the
 * mobile app all read `estimated_minutes` and none of them should have to
 * know that a bid was measured rather than typed.
 */
export function taskMinutes(
  service: ServiceCalculationInput,
  room: Pick<RoomCalculationInput, 'measuredSquareFeet'>,
): number {
  const basis = service.measurementBasis ?? 'time';
  if (basis === 'time') return Math.max(0, Math.trunc(service.estimatedMinutes));

  return measuredTaskMinutes({
    // An 'area' task with no quantity of its own is measured over the area it
    // sits on; a 'fixture' task has nothing to inherit and counts zero.
    measuredQuantity: service.measuredQuantity ?? (basis === 'area' ? (room.measuredSquareFeet ?? null) : null),
    productionRatePerHour: service.productionRatePerHour,
  });
}

/**
 * What an area's square footage is actually doing to this room — which is not
 * always what a filled-in box implies.
 *
 * The square-feet field shows on every commercial area, but the figure only
 * reaches money through a task that reads it, and there are three ordinary
 * ways to end up with none:
 *
 *   * every task is TIMED. A task typed by hand starts timed, and every
 *     service template written before measured estimating reads timed
 *     (migration 0086 defaults the column). A room built from a company's own
 *     library is all timed, and the square footage changes nothing.
 *   * an area task carries its OWN quantity, so it measures over that and
 *     never looks at the room's figure.
 *   * an area task has no RATE. Square feet per hour is what turns a
 *     measurement into minutes, and without it the line is zero minutes —
 *     `measuredTaskMinutes` refuses to invent one.
 *
 * And a room charged at a FLAT PRICE is a fourth case of its own: its tasks
 * still read the area and the minutes still drive the schedule and the work
 * order, but they no longer reach the labor subtotal. The figure is doing
 * real work there — just not to the price — and saying "unused" would be a
 * lie in the other direction.
 *
 * Lives here, beside `taskMinutes`, because it is a claim ABOUT the pricing
 * rules: put it in a component and the day somebody changes what a measured
 * line reads, the screen keeps making the old promise.
 */
export type MeasuredAreaEffect =
  /** The ordinary case: change the area, change the money. */
  | 'drives_price'
  /** Flat-priced room — the measurement still moves the time, not the charge. */
  | 'drives_time_only'
  /** The room's minutes are pinned by a typed adjustment, so nothing derived moves them. */
  | 'pinned_by_override'
  /** A measured task is here, but it carries its own quantity and ignores the room's. */
  | 'overridden_on_tasks'
  /** A measured task is here and would read the figure, but it is switched off. */
  | 'task_not_selected'
  /** Nothing here measures by floor area at all. */
  | 'unused';

export function measuredAreaEffect(
  room: Pick<RoomCalculationInput, 'flatPriceCents' | 'adjustedMinutes' | 'services'>,
): MeasuredAreaEffect {
  /*
    Reported as the FIRST blocker between "typed a number" and "the price
    moved", in the order the estimator would hit them:

      1. does anything here read the figure at all?
      2. are the room's minutes pinned by hand?
      3. is the room charged a flat price?

    Order matters more than it looks. Leading with the pinned minutes would
    tell somebody whose tasks are all timed to clear the adjustment — and
    having done it, they would watch the price not move, because nothing was
    measuring by area either. Each answer has to be the nearest true one, not
    merely a true one; clearing the second blocker surfaces the third, which
    is a short honest journey rather than one compound sentence nobody reads.
  */
  const isArea = (service: ServiceCalculationInput) => (service.measurementBasis ?? 'time') === 'area';
  const areaTasks = room.services.filter((service) => service.isSelected && isArea(service));
  // `??` is what taskMinutes uses, so 0 counts as a real quantity of zero and
  // only null/absent falls through to the room's own figure.
  const inherits = areaTasks.filter(
    (service) => (service.measuredQuantity ?? null) === null && (service.productionRatePerHour ?? 0) > 0,
  );

  if (inherits.length === 0) {
    /*
      Told apart because the fix is different, and telling somebody to switch
      a task to Area when it is already Area is worse than saying nothing. A
      task measuring over its own square footage is doing so deliberately;
      what is idle is the room's figure, and clearing the task's own quantity
      is what hands it back.
    */
    const measuresItsOwn = areaTasks.some(
      (service) => (service.measuredQuantity ?? null) !== null && (service.productionRatePerHour ?? 0) > 0,
    );
    if (measuresItsOwn) return 'overridden_on_tasks';

    // A task that would read the figure and is simply unticked. Told apart
    // for the same reason as the one above: it is already Area with a rate,
    // so "switch one to Area, or give it a rate" is an instruction with
    // nothing left to carry out. Ticking it on is the whole of the fix.
    const switchedOff = room.services.some(
      (service) =>
        !service.isSelected &&
        isArea(service) &&
        (service.measuredQuantity ?? null) === null &&
        (service.productionRatePerHour ?? 0) > 0,
    );
    return switchedOff ? 'task_not_selected' : 'unused';
  }

  /*
    Something reads the figure, so the room's own two switches decide what it
    reaches. A typed adjustment replaces the derived duration outright, which
    freezes the room's billable MINUTES — the claim the hint makes. It does
    not freeze the quote's total in every case: on a monthly quote the pinned
    minutes are still shared out across each task's frequency, so shifting the
    measurement shifts that share. Which is why the message stops at the time
    and does not promise anything about the price.
  */
  if (room.adjustedMinutes !== null && room.adjustedMinutes !== undefined && Number.isFinite(room.adjustedMinutes)) {
    return 'pinned_by_override';
  }

  return room.flatPriceCents === null || room.flatPriceCents === undefined ? 'drives_price' : 'drives_time_only';
}

/** Spec §9.3–§9.5. */
export function calculateRoom(room: RoomCalculationInput): RoomCalculationResult {
  const quantity = Math.max(1, Math.trunc(room.quantity || 1));

  const selectedMinutes = room.services.reduce(
    (total, service) => (service.isSelected ? total + taskMinutes(service, room) : total),
    0,
  );

  const baseMinutes = saturatingMul(selectedMinutes, quantity);

  // Multiply in basis points, then divide once, so intermediate rounding never
  // creeps into the result: 60 × 1.25 × 1.25 = 93.75 → 94 (spec §9.4).
  // The product is exact integer arithmetic. A quantity of 1,000 times a long
  // task times two 1.5× multipliers no longer rounds in floating point before
  // the scale-down, which was off by a minute on rooms the schema allows.
  const calculatedMinutes = saturatingProductDiv(
    [selectedMinutes, quantity, room.sizeMultiplierBasisPoints, room.conditionMultiplierBasisPoints],
    BASIS_POINTS_SCALE * BASIS_POINTS_SCALE,
  );

  const hasAdjustment =
    room.adjustedMinutes !== null && room.adjustedMinutes !== undefined && Number.isFinite(room.adjustedMinutes);

  const billableMinutes = hasAdjustment ? Math.max(0, Math.trunc(room.adjustedMinutes as number)) : calculatedMinutes;

  /*
    The taxable share of this room's time (spec §9.10).

    Apportioned rather than recomputed, because a room's billable minutes are
    not the sum of its tasks: size and condition multiply them, and an
    estimator's manual adjustment replaces them outright. Splitting the result
    in the ratio the tasks themselves are in keeps the two parts adding back up
    to the whole however the room got to its number.

    A room with no selected minutes but an adjustment on it — someone typed a
    duration onto an empty room — has no ratio to apportion by. That time stays
    taxable: labor is taxable unless a task says otherwise, and there is no
    task here saying otherwise.
  */
  const taxableSelectedMinutes = room.services.reduce(
    (total, service) =>
      service.isSelected && service.isTaxable !== false ? total + taskMinutes(service, room) : total,
    0,
  );

  return {
    baseMinutes,
    calculatedMinutes,
    billableMinutes,
    isManuallyAdjusted: hasAdjustment,
    chargeCents: calculateRoomChargeCents(room),
    taxableBaseMinutes: saturatingMul(taxableSelectedMinutes, quantity),
    groupQuantity: clampGroupQuantity(room.groupQuantity),
  };
}

/**
 * A flat-priced room's contribution, in cents (spec §9.12).
 *
 * The price is per room, so quantity multiplies it — three $40 bedrooms are
 * $120 — and size and condition then multiply it the same way they multiply
 * minutes, in basis points with a single divide at the end. A large, heavily
 * soiled $45 bathroom is 45 × 1.25 × 1.25 = $70.31.
 *
 * Exported because a saved quote is read back one room at a time, and that
 * path must not re-derive this arithmetic and risk disagreeing with the engine.
 */
export function calculateRoomChargeCents(room: {
  readonly flatPriceCents?: number | null;
  readonly quantity: number;
  readonly sizeMultiplierBasisPoints: number;
  readonly conditionMultiplierBasisPoints: number;
}): number | null {
  const price = room.flatPriceCents;
  if (price === null || price === undefined || !Number.isFinite(price)) return null;

  const quantity = Math.max(1, Math.trunc(room.quantity || 1));

  return saturatingProductDiv(
    [Math.max(0, Math.trunc(price)), quantity, room.sizeMultiplierBasisPoints, room.conditionMultiplierBasisPoints],
    BASIS_POINTS_SCALE * BASIS_POINTS_SCALE,
  );
}

// ---------------------------------------------------------------------------
// Quote-level calculation
// ---------------------------------------------------------------------------

/**
 * Multiply two magnitudes without leaving the safe integer range.
 *
 * For a product that IS the stored figure (minutes × group quantity, a flat
 * price × group quantity). A product that is only an intermediate — minutes ×
 * rate, before dividing by 60 — must use `saturatingProductDiv` instead.
 * Clamping here and dividing afterwards underprices a quote whose cents still
 * fit.
 */
function saturatingMul(left: number, right: number): number {
  if (!Number.isFinite(left) || !Number.isFinite(right)) return Number.MAX_SAFE_INTEGER;
  const a = Math.trunc(left);
  const b = Math.trunc(right);
  if (a === 0 || b === 0) return 0;
  const negative = a < 0 !== b < 0;
  const absA = Math.abs(a);
  const absB = Math.abs(b);
  if (absA > Math.floor(Number.MAX_SAFE_INTEGER / absB)) {
    return negative ? -Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  }
  return a * b;
}

/**
 * `(factors multiplied) ÷ divisor`, rounded half away from zero, then clamped.
 *
 * The division happens in exact integer arithmetic before any clamp. A
 * 100,000-minute task × a group of 10,000 at $1,000,000/hr is 1e17 cent-minutes;
 * divided by 60 that is still a safe integer, and saturating the product first
 * would store a much smaller price.
 */
function saturatingProductDiv(factors: readonly number[], divisor: number): number {
  if (!Number.isFinite(divisor) || divisor === 0) return Number.MAX_SAFE_INTEGER;
  let negative = divisor < 0;
  let product = 1n;
  for (const factor of factors) {
    if (!Number.isFinite(factor)) return Number.MAX_SAFE_INTEGER;
    const value = BigInt(Math.trunc(factor));
    if (value < 0n) negative = !negative;
    product *= value < 0n ? -value : value;
  }
  const scale = BigInt(Math.trunc(Math.abs(divisor)));
  const quotient = product / scale;
  const remainder = product % scale;
  const rounded = remainder * 2n >= scale ? quotient + 1n : quotient;
  const signed = negative ? -rounded : rounded;
  const cap = BigInt(Number.MAX_SAFE_INTEGER);
  if (signed > cap) return Number.MAX_SAFE_INTEGER;
  if (signed < -cap) return -Number.MAX_SAFE_INTEGER;
  return Number(signed);
}

/** Add two magnitudes without leaving the safe integer range. */
function saturatingAdd(left: number, right: number): number {
  if (!Number.isFinite(left) || !Number.isFinite(right)) return Number.MAX_SAFE_INTEGER;
  if (right > 0 && left > Number.MAX_SAFE_INTEGER - right) return Number.MAX_SAFE_INTEGER;
  if (right < 0 && left < -Number.MAX_SAFE_INTEGER - right) return -Number.MAX_SAFE_INTEGER;
  return left + right;
}

/** Spec §9.7 — labor price from billable minutes and the hourly rate. */
export function calculateLaborCents(billableMinutes: number, hourlyRateCents: number): number {
  if (Number.isSafeInteger(billableMinutes)) {
    return laborCentsFromExactMinutes(BigInt(billableMinutes), hourlyRateCents);
  }
  return saturatingProductDiv([billableMinutes, hourlyRateCents], 60);
}

/**
 * What a visit costs, before anybody types over it: the booking form's opening
 * answer to "price per visit".
 *
 * There are only two honest sources for that number, and which one applies is
 * decided by how the booking began. Booked from a quote, the price is the
 * quoted total — the customer has been shown a figure and, on an accepted
 * quote, agreed to it, so anything else would bill them something they never
 * saw. Booked without one, nothing has been sold yet, and the closest thing to
 * a price is the company's own arithmetic: its hourly rate for as long as the
 * visit is scheduled to take.
 *
 * A suggestion only, in both cases. The field stays editable and the office
 * has the last word — a schedule is not a quote, and standing work is
 * routinely priced differently from the one-off that started it.
 *
 * `null` means "suggest nothing", and is deliberately not the same answer as
 * zero: a blank price leaves billing outside the app, while a zero says this
 * visit is free. Which is why an unset hourly rate suggests nothing rather
 * than suggesting free work, and why an amount outside what the field itself
 * accepts — too small for a card to charge, or past the cap every money field
 * on this system shares — is left out rather than pre-filled into a form that
 * would refuse to save it.
 */
export function suggestedVisitPriceCents(input: {
  /** The quoted total when booking from a quote; `null` when booking without one. */
  readonly quotedTotalCents: number | null;
  readonly durationMinutes: number;
  readonly hourlyRateCents: number;
  readonly currencyCode?: string;
}): number | null {
  const currencyCode = input.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const step = (cents: number) => roundToCurrencyStep(cents, currencyCode);
  const floor = minimumChargeCents(currencyCode);
  /*
    Above the ceiling there is nothing to suggest either, and the arithmetic
    gets there on its own: a legal hourly rate is capped at the same figure, so
    any visit over an hour at the top of the range clears it. Pre-filling that
    would hand the office a form it cannot save until the number is deleted by
    hand — the sub-chargeable case again, from the other end.
  */
  const withinRange = (cents: number) => cents >= floor && cents <= MAX_AMOUNT_CENTS;

  if (input.quotedTotalCents !== null) {
    // Stepped like every other amount that arrives from elsewhere: a quote
    // priced before a currency change carries cents a whole-unit currency
    // cannot charge.
    const quoted = step(Math.trunc(input.quotedTotalCents));
    // A quote that came to nothing is a real answer — the visit is free — but
    // a total this form could not save is not one to put in it.
    if (quoted === 0) return 0;
    return withinRange(quoted) ? quoted : null;
  }

  const minutes = Math.max(0, Math.trunc(input.durationMinutes));
  const rate = Math.max(0, Math.trunc(input.hourlyRateCents));
  const estimate = step(calculateLaborCents(minutes, rate));
  // No rate configured, or a visit with no length yet: the estimate is zero
  // because nothing is known, not because the work is free.
  return withinRange(estimate) ? estimate : null;
}

/** Spec §9.9 — a discount never exceeds the amount it is discounting. */
export function calculateDiscountCents(
  discountableCents: number,
  discountType: DiscountType | null,
  discountValue: number,
): number {
  if (!discountType || discountValue <= 0 || discountableCents <= 0) return 0;

  const raw =
    discountType === 'percentage'
      ? roundHalfUp((discountableCents * discountValue) / BASIS_POINTS_SCALE)
      : Math.trunc(discountValue);

  return Math.min(Math.max(0, raw), discountableCents);
}

/**
 * Full quote calculation (spec §9.6–§9.11).
 *
 * Tax note: spec §9.10 defines the taxable subtotal as
 * `taxable labor + taxable fees - applicable discount`. A discount applies to
 * the whole quote, so the "applicable" share is the part that falls on taxable
 * lines, allocated in proportion to their value. When everything is taxable
 * this reduces to the plain formula.
 *
 * Currency note: three of the amounts below are *derived* — labor from a rate
 * and a duration, the discount and the tax from percentages — so each can land
 * between the units its currency actually has. Half an hour at ¥1,001/hr is
 * ¥500.50, which no one can be charged and which would be discovered only when
 * the card was presented, long after the customer approved the quote. Each is
 * therefore rounded to the currency's own step, which keeps every line and the
 * total on amounts that exist. For an ordinary two-decimal currency the step is
 * one cent and every line below is exactly what it always was.
 *
 * Fees are rounded to the same step even though they are typed rather than
 * derived. The amount fields do snap on entry, but that is not the only way a
 * fee arrives: duplicating a quote after a currency change copies the source's
 * fees across unchanged, and an import or a direct API write answers to no
 * input control at all. Leaving them alone made the total's step conditional
 * on where the fee came from — and a ¥10.50 fee, already displayed as ¥11,
 * only surfaced when the approved quote was refused at the till.
 */
export function calculateQuote(input: QuoteCalculationInput): QuoteCalculationResult {
  const rooms = input.rooms.map(calculateRoom);
  const step = (cents: number) => roundToCurrencyStep(cents, input.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const billingBasis: BillingBasis = input.billingBasis ?? 'per_visit';
  const basis =
    billingBasis === 'monthly' ? monthlyLaborBasis(input, rooms, step) : perVisitLaborBasis(rooms, input.rooms, step);

  const { estimatedMinutes, routineVisitMinutes, chargeableWeight, taxableChargeableWeight, roomChargesCents } = basis;
  const billedExact = applyBillingIncrementExact(basis.chargeableMinutesExact, input.billingIncrementMinutes);
  const billableMinutes = Number.isFinite(Number(billedExact)) ? Number(billedExact) : Number.MAX_SAFE_INTEGER;

  // Stepped for the same reason fees are: the rate is typed in, but not always
  // through a control that snapped it. Duplicating a quote after a currency
  // change copies the source's rate across untouched, and a $10.50 rate would
  // then render as ¥11 while labor was still computed from ¥10.50.
  const hourlyRateCents = step(Math.max(0, Math.trunc(input.hourlyRateCents)));
  const dustAllocationBlocked =
    !!input.dustUpliftBasisPoints &&
    input.laborIsTaxable &&
    input.rooms.some((room) => room.services.some((task) => task.isSelected && task.isTaxable === false));
  let pricingBlocked = input.pricingBlocked || dustAllocationBlocked;
  const area = pricingBlocked ? null : (input.areaPricing ?? null);
  if (
    area &&
    input.rooms.some(
      (room) =>
        room.flatPriceCents != null || room.services.some((task) => task.isSelected && task.isTaxable === false),
    )
  ) {
    throw new RangeError('Area pricing cannot combine flat room prices or exempt tasks.');
  }
  if (
    area &&
    (!Number.isSafeInteger(area.squareFeet) ||
      area.squareFeet < 1 ||
      area.squareFeet > 1_000_000 ||
      !Number.isSafeInteger(area.rateCents) ||
      area.rateCents < 1 ||
      BigInt(area.squareFeet) * BigInt(area.rateCents) > 100_000_000n)
  ) {
    throw new RangeError('Area pricing needs a valid measurement and a supported subtotal.');
  }
  const laborSubtotalCents = step(
    area ? areaLaborCents(area, input) : laborCentsFromExactMinutes(billedExact, hourlyRateCents),
  );

  let dustAdjustmentCents = 0;
  let dustAmountBlocked = false;
  if (!pricingBlocked && input.dustUpliftBasisPoints) {
    try {
      dustAdjustmentCents = dustUpliftCents(
        laborSubtotalCents,
        input.dustUpliftBasisPoints,
        input.currencyCode ?? DEFAULT_CURRENCY_CODE,
      );
    } catch (error) {
      if (!(error instanceof RangeError)) throw error;
      pricingBlocked = true;
      dustAmountBlocked = true;
    }
  }
  const feeCents = (fee: FeeCalculationInput) => step(Math.trunc(fee.amountCents));
  const fixedFeesCents = input.fees.reduce((total, fee) => total + feeCents(fee), dustAdjustmentCents);
  const taxableFeesCents = input.fees.reduce(
    (total, fee) => (fee.isTaxable ? total + feeCents(fee) : total),
    input.laborIsTaxable ? dustAdjustmentCents : 0,
  );

  const discountableCents = laborSubtotalCents + roomChargesCents + fixedFeesCents;
  // Stepped, then re-capped: rounding up must not let the discount exceed the
  // amount it is discounting.
  const discountCents = Math.min(
    step(calculateDiscountCents(discountableCents, input.discountType, input.discountValue)),
    Math.max(0, discountableCents),
  );

  // The frequency tier's reduction, computed off the same base so percentages
  // stack additively, and capped at what the quote's own discount left — the
  // two together never exceed the amount being discounted.
  const tier = input.frequencyTierDiscount ?? null;
  const frequencyDiscountCents = tier
    ? Math.min(
        step(calculateDiscountCents(discountableCents, tier.discountType, tier.discountValue)),
        Math.max(0, discountableCents - discountCents),
      )
    : 0;
  // Every rule downstream — the taxable allocation, the total — treats the two
  // as one combined reduction; they are only *reported* apart.
  const combinedDiscountCents = discountCents + frequencyDiscountCents;

  /*
    The part of labor that tax applies to (spec §9.10).

    Two gates, and they are different questions. `laborIsTaxable` is the
    company's answer for cleaning labor as such — off, and no labor on this
    quote is taxable whatever its tasks say. Within labor that *is* taxable, a
    task marked exempt takes its share out.

    The share is taken out of the money, not recomputed from minutes, so the
    taxable and exempt parts always add back up to `laborSubtotalCents`
    exactly. Recomputing would round each part separately and leave the two
    disagreeing with the labor line the customer is reading.

    Rounding up to the billing increment is a whole-quote adjustment, so its
    cost is shared in the same proportion — an exempt task does not get to
    absorb all of it, and does not escape all of it either.
  */
  const taxableLaborSubtotalCents =
    chargeableWeight > 0
      ? roundHalfUp((laborSubtotalCents * taxableChargeableWeight) / chargeableWeight)
      : laborSubtotalCents;

  // A room's price is the price of cleaning that room — the same supply of
  // labor the hourly rate bills for, arrived at differently — so it follows
  // whatever the company answered for labor rather than being taxed on its own
  // rule. A jurisdiction that does not tax cleaning labor does not start
  // taxing it because the company quotes by the room.
  //
  // Which is also why a task marked exempt does not carve up a flat price: that
  // price is a price for the room, not a sum of its tasks, and there is no
  // honest ratio to split it by. Exempting work inside a flat-priced room means
  // pricing that room by time.
  const taxableLaborCents = input.laborIsTaxable ? taxableLaborSubtotalCents + roomChargesCents : 0;
  const taxableBeforeDiscountCents = taxableLaborCents + taxableFeesCents;

  // Allocate the discount across taxable and non-taxable value.
  //
  // The weights have to be non-negative: fees may be negative (a credit), and a
  // non-taxable credit makes `discountableCents` smaller than the taxable value
  // it is being divided into. The raw ratio then exceeds 1 and would allocate
  // more discount to the taxable lines than the quote grants in total — $100 of
  // taxable labor, a −$50 non-taxable credit and a $10 discount produced $20 of
  // taxable relief. Capping at `discountCents` keeps the allocation a share of
  // the discount rather than a multiple of it.
  const discountWeightCents = Math.max(0, discountableCents);
  const taxableWeightCents = Math.max(0, Math.min(taxableBeforeDiscountCents, discountWeightCents));

  const applicableDiscountCents =
    discountWeightCents > 0
      ? Math.min(
          roundHalfUp((combinedDiscountCents * taxableWeightCents) / discountWeightCents),
          taxableBeforeDiscountCents,
          combinedDiscountCents,
        )
      : 0;

  const taxableSubtotalCents = Math.max(0, taxableBeforeDiscountCents - applicableDiscountCents);

  const taxRateBasisPoints = input.taxEnabled ? Math.max(0, Math.trunc(input.taxRateBasisPoints)) : 0;
  const taxCents = step(saturatingProductDiv([taxableSubtotalCents, taxRateBasisPoints], BASIS_POINTS_SCALE));

  // Spec §9.11. Each addend is already inside the safe integer range; the sum
  // has to stay there too, or a quote of several saturated lines would wrap.
  const totalCents = saturatingAdd(
    saturatingAdd(saturatingAdd(laborSubtotalCents, roomChargesCents), fixedFeesCents) - combinedDiscountCents,
    taxCents,
  );

  return {
    rooms,
    billingBasis,
    routineVisitMinutes,
    estimatedMinutes,
    billableMinutes,
    laborSubtotalCents,
    roomChargesCents,
    fixedFeesCents,
    dustAdjustmentCents,
    ...(dustAmountBlocked ? { dustAmountBlocked: true } : {}),
    discountCents,
    frequencyDiscountCents,
    taxableSubtotalCents,
    taxCents,
    totalCents,
    ...(pricingBlocked
      ? {
          pricingBlocked: true,
          dustAdjustmentCents: 0,
          laborSubtotalCents: 0,
          roomChargesCents: 0,
          fixedFeesCents: 0,
          discountCents: 0,
          frequencyDiscountCents: 0,
          taxableSubtotalCents: 0,
          taxCents: 0,
          totalCents: 0,
        }
      : {}),
  };
}

// ---------------------------------------------------------------------------
// Frequency pricing tiers
// ---------------------------------------------------------------------------

/** One offered frequency and its adjustment off the quote as built. */
export interface QuoteFrequencyTierInput {
  readonly frequency: SeriesFrequency;
  readonly discountType: DiscountType | null;
  /** Cents when 'fixed', basis points when 'percentage'. */
  readonly discountValue: number;
}

export interface QuoteFrequencyTierResult extends QuoteFrequencyTierInput {
  /** The whole calculation at this tier — its total is the tier's price. */
  readonly result: QuoteCalculationResult;
}

/**
 * Every offered tier, each priced through the full engine, so a tier's total
 * carries the same tax treatment as the quote it discounts. The caller decides
 * which tiers are offered; this prices whatever it is handed, in the order it
 * is handed it.
 */
export function calculateFrequencyTiers(
  input: QuoteCalculationInput,
  tiers: readonly QuoteFrequencyTierInput[],
): QuoteFrequencyTierResult[] {
  return tiers.map((tier) => ({
    ...tier,
    result: calculateQuote({
      ...input,
      frequencyTierDiscount: { discountType: tier.discountType, discountValue: tier.discountValue },
    }),
  }));
}

// ---------------------------------------------------------------------------
// Labor bases: what the quote's minutes and room charges mean
// ---------------------------------------------------------------------------

/**
 * The intermediate every downstream line (labor, fees, discount, tax) prices
 * from, so the two billing bases can differ HERE and nowhere else.
 *
 * `chargeableWeight` / `taxableChargeableWeight` exist only to be divided into
 * each other for the taxable share of labor, so their unit is free — actual
 * minutes per visit under 'per_visit', frequency-weighted minutes per year
 * under 'monthly'. Deliberately unrounded, for the reason the per-visit code
 * has always documented: they are apportionment weights on their way to a
 * single division, and precision dropped here multiplies into cents later.
 */
interface LaborBasis {
  /** Whole-job time for display: the visit, or the month. */
  readonly estimatedMinutes: number;
  /** The ordinary visit's length — the schedule's number. */
  readonly routineVisitMinutes: number;
  /** Integer minutes labor is charged for, before the billing increment. */
  readonly chargeableMinutes: number;
  /**
   * The same minutes before they become a JS number. A month can be larger
   * than `MAX_SAFE_INTEGER`; labor is priced from this count, not from the
   * rounded double in `chargeableMinutes`.
   */
  readonly chargeableMinutesExact: bigint;
  readonly chargeableWeight: number;
  readonly taxableChargeableWeight: number;
  /** Flat-priced rooms' contribution, already stepped and summed. */
  readonly roomChargesCents: number;
}

/** The engine's original arithmetic, verbatim — one visit, priced whole. */
function perVisitLaborBasis(
  rooms: readonly RoomCalculationResult[],
  inputs: readonly RoomCalculationInput[],
  step: (cents: number) => number,
): LaborBasis {
  const scaleOf = (index: number) => clampGroupQuantity(inputs[index]?.groupQuantity ?? rooms[index]?.groupQuantity);
  // Every room's time counts towards how long the job takes, and a group of
  // forty of that room counts forty times.
  const estimatedMinutes = rooms.reduce(
    (total, room, index) => saturatingAdd(total, saturatingMul(room.billableMinutes, scaleOf(index))),
    0,
  );

  // ...but only a room being charged for its time contributes to labor. A
  // flat-priced room that kept its minutes here would be paid for twice: once
  // in the hourly rate and once in its own price.
  const chargeableMinutes = rooms.reduce(
    (total, room, index) =>
      room.chargeCents === null ? saturatingAdd(total, saturatingMul(room.billableMinutes, scaleOf(index))) : total,
    0,
  );
  /*
    The same minutes as an exact sum. Each room's billable time still fits in
    a safe integer — the per-room product is clamped there — but twenty of
    those rooms in a group of ten thousand do not. Adding them as JS numbers
    stops at `MAX_SAFE_INTEGER` and the quote underbills. Labor is priced from
    the bigint; `chargeableMinutes` above stays the clamped figure the screen
    can show.

    The taxable share is the same exact total, apportioned by each room's own
    taxable proportion, then reduced to a ratio. One taxable minute in six
    stays 1:6 all the way to the cent rounding, instead of a float that no
    longer matches the minutes labor was priced from once the sum overflows.
  */
  let chargeableExactTotal = zeroMinutes();
  let taxableExactTotal = zeroMinutes();
  for (let index = 0; index < rooms.length; index += 1) {
    const room = rooms[index];
    if (!room || room.chargeCents !== null) continue;
    const scale = scaleOf(index);
    chargeableExactTotal = addMinuteProduct(chargeableExactTotal, [room.billableMinutes, scale]);
    // No selected minutes to apportion by — time typed onto an empty room —
    // stays taxable, there being no task saying otherwise.
    taxableExactTotal =
      room.baseMinutes === 0
        ? addMinuteProduct(taxableExactTotal, [room.billableMinutes, scale])
        : addMinuteProduct(taxableExactTotal, [room.billableMinutes, room.taxableBaseMinutes, scale], room.baseMinutes);
  }
  const weights = minuteWeights(chargeableExactTotal, taxableExactTotal);

  // A per-room charge is typed in, like a fee, so it gets the same treatment:
  // stepped to the currency, because nothing guarantees it arrived through an
  // input control that already snapped it.
  const roomChargesCents = rooms.reduce(
    (total, room, index) =>
      saturatingAdd(total, room.chargeCents === null ? 0 : step(saturatingMul(room.chargeCents, scaleOf(index)))),
    0,
  );

  return {
    estimatedMinutes,
    routineVisitMinutes: estimatedMinutes,
    chargeableMinutes,
    chargeableMinutesExact: exactMinuteTotal(chargeableExactTotal, 1),
    chargeableWeight: weights.chargeable,
    taxableChargeableWeight: weights.taxable,
    roomChargesCents,
  };
}

/** Exact non-negative minutes as a reduced fraction. */
interface MinuteTotal {
  n: bigint;
  d: bigint;
}

function zeroMinutes(): MinuteTotal {
  return { n: 0n, d: 1n };
}

function minuteGcd(left: bigint, right: bigint): bigint {
  let a = left < 0n ? -left : left;
  let b = right < 0n ? -right : right;
  while (b !== 0n) {
    const next = a % b;
    a = b;
    b = next;
  }
  return a === 0n ? 1n : a;
}

/**
 * Adds `(factors multiplied) ÷ divisor` to a running minute total.
 *
 * Every factor is a whole number of minutes, copies, or occurrences. The
 * divisor is the room's base minutes when a size or condition factor is in
 * play, and 1 otherwise. The sum is reduced so a quote of hundreds of rooms
 * does not build an unbounded numerator.
 */
function addMinuteProduct(total: MinuteTotal, factors: readonly number[], divisor = 1): MinuteTotal {
  if (!Number.isFinite(divisor) || divisor === 0) return total;
  let num = 1n;
  for (const factor of factors) {
    if (!Number.isFinite(factor)) return total;
    const value = Math.trunc(factor);
    if (value === 0) return total;
    if (value < 0) return total;
    num *= BigInt(value);
  }
  let den = BigInt(Math.trunc(Math.abs(divisor)));
  if (den === 0n) return total;
  const shared = minuteGcd(num, den);
  num /= shared;
  den /= shared;
  let n = total.n * den + num * total.d;
  let d = total.d * den;
  const reduced = minuteGcd(n, d);
  n /= reduced;
  d /= reduced;
  return { n, d };
}

/**
 * Half-up `(total ÷ extraDivisor)`.
 *
 * The rounding happens on the exact integer. Only then does the result become
 * a number, so a total past `MAX_SAFE_INTEGER` keeps the nearest representable
 * value instead of being clamped first and losing a minute.
 */
function exactMinuteTotal(total: MinuteTotal, extraDivisor: number): bigint {
  const extra = BigInt(Math.max(1, Math.trunc(extraDivisor)));
  const den = total.d * extra;
  if (den === 0n) return BigInt(Number.MAX_SAFE_INTEGER);
  const quotient = total.n / den;
  const remainder = total.n % den;
  const rounded = remainder * 2n >= den ? quotient + 1n : quotient;
  return rounded < 0n ? 0n : rounded;
}

function roundMinuteTotal(total: MinuteTotal, extraDivisor: number): number {
  const rounded = exactMinuteTotal(total, extraDivisor);
  const asNumber = Number(rounded);
  return Number.isFinite(asNumber) ? asNumber : Number.MAX_SAFE_INTEGER;
}

/**
 * The taxable share of labor only needs the ratio of these two totals.
 *
 * Reduced, then scaled into the safe integer range together, so a year of
 * minutes that does not fit in a JS number still taxes the same fraction it
 * would have if the division were done on the exact totals.
 */
function minuteWeights(chargeable: MinuteTotal, taxable: MinuteTotal): { chargeable: number; taxable: number } {
  let charge = chargeable.n * taxable.d;
  let tax = taxable.n * chargeable.d;
  const shared = minuteGcd(charge, tax);
  charge /= shared;
  tax /= shared;
  const cap = BigInt(Number.MAX_SAFE_INTEGER);
  if (charge > cap || tax > cap) {
    const scale = (charge > tax ? charge : tax) / cap + 1n;
    charge /= scale;
    tax /= scale;
  }
  return { chargeable: Number(charge), taxable: Number(tax) };
}

/**
 * A month of the specified service (spec: commercial mode).
 *
 * Each task's minutes are weighted by how many times a year it happens —
 * `visitsPerWeek × 52` for every-visit work, the task's own frequency for the
 * rest, capped at the visit count because nothing can be done more often than
 * someone is there — and the year is divided by twelve exactly once, at the
 * end. A task's share of a room's time is apportioned through the room's own
 * base→billable factor, so size and condition multipliers and an estimator's
 * manual adjustment weigh on a quarterly task exactly as they weigh on a
 * nightly one.
 *
 * Flat-priced rooms are per-visit prices whatever their tasks say (a flat
 * price is a price for the room, not a sum of its tasks — the same reasoning
 * that keeps exempt tasks from carving one up), so a month buys
 * `visitsPerWeek × 52 / 12` of them.
 */
/**
 * Square feet times the rate is one visit's price. A monthly quote buys the
 * visits a month holds, counted the way hourly monthly labor counts them:
 * visits a week times 52, divided by 12 once (weekly is about 4.33 visits).
 */
export function areaLaborCents(
  area: { readonly squareFeet: number; readonly rateCents: number },
  input: Pick<QuoteCalculationInput, 'billingBasis' | 'visitsPerWeek'>,
): number {
  if ((input.billingBasis ?? 'per_visit') !== 'monthly')
    return saturatingProductDiv([area.squareFeet, area.rateCents], 1);
  const visitsPerWeek = Math.min(7, Math.max(1, Math.trunc(input.visitsPerWeek ?? 1)));
  return saturatingProductDiv([area.squareFeet, area.rateCents, visitsPerWeek * 52], 12);
}

function monthlyLaborBasis(
  input: QuoteCalculationInput,
  rooms: readonly RoomCalculationResult[],
  step: (cents: number) => number,
): LaborBasis {
  const visitsPerWeek = Math.min(7, Math.max(1, Math.trunc(input.visitsPerWeek ?? 1)));
  const everyVisitPerYear = visitsPerWeek * 52;
  const occurrencesPerYear = (frequency: ServiceFrequency | undefined): number =>
    frequency === undefined || frequency === 'every_visit'
      ? everyVisitPerYear
      : Math.min(FREQUENCY_OCCURRENCES_PER_YEAR[frequency], everyVisitPerYear);

  // Annual totals stay exact until the single ÷ 12. A year of group-scaled
  // minutes overflows a JS number (200 rooms × quantity 1,000 × group 10,000
  // × two 100,000-minute tasks × 7 visits is 1.456e17), and dividing that
  // rounded float is a minute off the half-up result.
  let annualMinutes = zeroMinutes();
  let annualChargeableMinutes = zeroMinutes();
  let annualTaxableMinutes = zeroMinutes();
  let routineMinutes = zeroMinutes();

  rooms.forEach((room, index) => {
    const roomInput = input.rooms[index];
    if (!roomInput) return;
    const flatPriced = room.chargeCents !== null;
    const groupQuantity = clampGroupQuantity(roomInput.groupQuantity);

    // Minutes typed onto an empty room: every-visit and taxable, there being
    // no task saying otherwise — the same rule the per-visit basis applies.
    // The group's copies are identical visits, so they scale the empty room
    // the same way they scale a room built from tasks.
    if (room.baseMinutes === 0) {
      routineMinutes = addMinuteProduct(routineMinutes, [room.billableMinutes, groupQuantity]);
      annualMinutes = addMinuteProduct(annualMinutes, [room.billableMinutes, groupQuantity, everyVisitPerYear]);
      if (!flatPriced) {
        annualChargeableMinutes = addMinuteProduct(annualChargeableMinutes, [
          room.billableMinutes,
          groupQuantity,
          everyVisitPerYear,
        ]);
        annualTaxableMinutes = addMinuteProduct(annualTaxableMinutes, [
          room.billableMinutes,
          groupQuantity,
          everyVisitPerYear,
        ]);
      }
      return;
    }

    // How the room's base minutes became its billable minutes — multipliers
    // and any manual adjustment, as one factor every task shares. The division
    // by base minutes stays inside the exact product so it is not rounded
    // before the year's minutes are summed.
    const quantity = Math.max(1, Math.trunc(roomInput.quantity || 1));

    for (const service of roomInput.services) {
      if (!service.isSelected) continue;
      const minutes = taskMinutes(service, roomInput);
      if (minutes === 0) continue;
      const perYear = occurrencesPerYear(service.frequency);
      const factors = [minutes, quantity, groupQuantity, room.billableMinutes, perYear];
      const routineFactors = [minutes, quantity, groupQuantity, room.billableMinutes];

      if (service.frequency === undefined || service.frequency === 'every_visit') {
        routineMinutes = addMinuteProduct(routineMinutes, routineFactors, room.baseMinutes);
      }
      annualMinutes = addMinuteProduct(annualMinutes, factors, room.baseMinutes);
      if (!flatPriced) {
        annualChargeableMinutes = addMinuteProduct(annualChargeableMinutes, factors, room.baseMinutes);
        if (service.isTaxable !== false) {
          annualTaxableMinutes = addMinuteProduct(annualTaxableMinutes, factors, room.baseMinutes);
        }
      }
    }
  });

  const visitsPerMonthNumerator = everyVisitPerYear; // ÷ 12 happens at each use, once
  const roomChargesCents = rooms.reduce((total, room, index) => {
    if (room.chargeCents === null) return total;
    const groupQuantity = clampGroupQuantity(input.rooms[index]?.groupQuantity);
    return saturatingAdd(
      total,
      step(saturatingProductDiv([room.chargeCents, groupQuantity, visitsPerMonthNumerator], 12)),
    );
  }, 0);

  const weights = minuteWeights(annualChargeableMinutes, annualTaxableMinutes);
  const chargeableExact = exactMinuteTotal(annualChargeableMinutes, 12);
  const chargeableAsNumber = Number(chargeableExact);
  return {
    estimatedMinutes: roundMinuteTotal(annualMinutes, 12),
    routineVisitMinutes: roundMinuteTotal(routineMinutes, 1),
    chargeableMinutes: Number.isFinite(chargeableAsNumber) ? chargeableAsNumber : Number.MAX_SAFE_INTEGER,
    chargeableMinutesExact: chargeableExact,
    chargeableWeight: weights.chargeable,
    taxableChargeableWeight: weights.taxable,
    roomChargesCents,
  };
}

/**
 * What one group contributes, before the quote's discount, tax and billing
 * increment.
 *
 * Room minutes and flat charges are the room's own (room quantity already
 * applied). The group's quantity multiplies both. Labor is priced from the
 * multiplied minutes in one rounding, the same way the quote prices labor,
 * so a group's subtotal is the figure a customer can check against its rooms.
 */
export function groupContribution(input: {
  readonly quantity: number;
  readonly hourlyRateCents: number;
  readonly rooms: readonly { readonly billableMinutes: number; readonly chargeCents: number | null }[];
}): { readonly minutes: number; readonly cents: number } {
  const quantity = clampGroupQuantity(input.quantity);
  let minutes = 0;
  let laborMinutes = 0;
  let flatCents = 0;
  for (const room of input.rooms) {
    const roomMinutes = Math.max(0, Math.trunc(room.billableMinutes));
    minutes += roomMinutes;
    if (room.chargeCents === null) laborMinutes += roomMinutes;
    else flatCents += Math.max(0, Math.trunc(room.chargeCents));
  }
  return {
    minutes: minutes * quantity,
    cents:
      calculateLaborCents(laborMinutes * quantity, Math.max(0, Math.trunc(input.hourlyRateCents))) +
      flatCents * quantity,
  };
}

/**
 * One group's share of a quote, on the same basis the quote is priced on.
 *
 * A monthly quote weights each task by how often it happens and buys
 * `visitsPerWeek × 52 / 12` of a flat room. Multiplying the one-visit figures
 * by the group quantity alone understates that month — a weekly $100 room
 * would show $100 beside a monthly total near $433. This runs the same
 * per-visit or monthly basis the quote uses, limited to these rooms, and
 * stops before the quote-wide discount, tax and billing increment.
 */
export function groupSubtotal(
  input: QuoteCalculationInput,
  roomIndexes: readonly number[],
): { readonly minutes: number; readonly cents: number } {
  const rooms = roomIndexes
    .map((index) => input.rooms[index])
    .filter((room): room is RoomCalculationInput => room !== undefined);
  const results = rooms.map(calculateRoom);
  const step = (cents: number) => roundToCurrencyStep(cents, input.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const basis =
    (input.billingBasis ?? 'per_visit') === 'monthly'
      ? monthlyLaborBasis({ ...input, rooms }, results, step)
      : perVisitLaborBasis(results, rooms, step);
  const rate = step(Math.max(0, Math.trunc(input.hourlyRateCents)));
  return {
    minutes: basis.estimatedMinutes,
    cents: input.pricingBlocked
      ? 0
      : input.areaPricing
        ? step(areaLaborCents(input.areaPricing, input))
        : laborCentsFromExactMinutes(basis.chargeableMinutesExact, rate) + basis.roomChargesCents,
  };
}

/**
 * Every group's subtotal, with labor rounded once for the quote.
 *
 * Pricing each group on its own rounds cents per group, and those cents do
 * not add back up to the labor line. The quote's pre-increment labor is
 * computed once and shared out by each group's exact chargeable minutes,
 * largest remainder first, so the subtotals reconcile with the amount quoted.
 * Flat room charges stay with their group; they were already stepped per room.
 */
export function groupSubtotals(
  input: QuoteCalculationInput,
  groups: readonly (readonly number[])[],
): { readonly minutes: number; readonly cents: number }[] {
  if (groups.length === 0) return [];
  if (input.pricingBlocked)
    return groups.map((indexes) => ({ minutes: groupSubtotal(input, indexes).minutes, cents: 0 }));
  if (input.areaPricing) {
    if (groups.length !== 1) throw new RangeError('Area pricing requires one unrepeated scope group.');
    return [groupSubtotal(input, groups[0] ?? [])];
  }
  const step = (cents: number) => roundToCurrencyStep(cents, input.currencyCode ?? DEFAULT_CURRENCY_CODE);
  const calculated = input.rooms.map(calculateRoom);
  const monthly = (input.billingBasis ?? 'per_visit') === 'monthly';
  const basis = monthly
    ? monthlyLaborBasis(input, calculated, step)
    : perVisitLaborBasis(calculated, input.rooms, step);
  const rate = step(Math.max(0, Math.trunc(input.hourlyRateCents)));
  const laborCents = laborCentsFromExactMinutes(basis.chargeableMinutesExact, rate);

  const parts = groups.map((indexes) => {
    const subset = groupSubtotal(input, indexes);
    const rooms = indexes
      .map((index) => input.rooms[index])
      .filter((room): room is RoomCalculationInput => room !== undefined);
    const results = rooms.map(calculateRoom);
    const partBasis = monthly
      ? monthlyLaborBasis({ ...input, rooms }, results, step)
      : perVisitLaborBasis(results, rooms, step);
    return { minutes: subset.minutes, weight: partBasis.chargeableMinutesExact, flats: partBasis.roomChargesCents };
  });

  const shares = allocateCents(
    laborCents,
    parts.map((part) => part.weight),
  );
  return parts.map((part, index) => ({
    minutes: part.minutes,
    cents: (shares[index] ?? 0) + part.flats,
  }));
}

/**
 * The lines under a set of group subtotals, in cents, that add up to the
 * figure the customer is asked to pay.
 *
 * Group subtotals stop before the billing increment, fees, discounts and tax,
 * because those belong to the quote once. Showing the groups beside the
 * headline without these lines leaves a gap (a 15-minute round-up at $65/hr
 * is $14.08) that looks like the total is wrong.
 *
 * `billingRoundingCents` is whatever sits between the group sum and labor
 * plus flat room charges — the billing increment, when the groups cover
 * every room. `adjustmentCents` is whatever is still left after that, which
 * is a manual price. Every other line is the quote's own figure.
 */
export interface ReconciledPrice {
  readonly subtotalCents: number;
  readonly billingRoundingCents: number;
  readonly feesCents: number;
  readonly discountCents: number;
  readonly frequencyDiscountCents: number;
  readonly taxCents: number;
  readonly adjustmentCents: number;
  readonly totalCents: number;
}

export function reconcilePrice(
  groupSubtotalCents: readonly number[],
  priced: Pick<
    QuoteCalculationResult,
    | 'laborSubtotalCents'
    | 'roomChargesCents'
    | 'fixedFeesCents'
    | 'discountCents'
    | 'frequencyDiscountCents'
    | 'taxCents'
    | 'totalCents'
  >,
  displayedTotalCents: number = priced.totalCents,
): ReconciledPrice {
  const subtotalCents = groupSubtotalCents.reduce((sum, cents) => sum + Math.trunc(cents), 0);
  const billingRoundingCents = priced.laborSubtotalCents + priced.roomChargesCents - subtotalCents;
  const beforeAdjustment =
    subtotalCents +
    billingRoundingCents +
    priced.fixedFeesCents -
    priced.discountCents -
    priced.frequencyDiscountCents +
    priced.taxCents;
  return {
    subtotalCents,
    billingRoundingCents,
    feesCents: priced.fixedFeesCents,
    discountCents: priced.discountCents,
    frequencyDiscountCents: priced.frequencyDiscountCents,
    taxCents: priced.taxCents,
    adjustmentCents: Math.trunc(displayedTotalCents) - beforeAdjustment,
    totalCents: Math.trunc(displayedTotalCents),
  };
}

/** Largest-remainder split of `total` cents across non-negative weights. */
function allocateCents(total: number, weights: readonly bigint[]): number[] {
  if (weights.length === 0 || total <= 0) return weights.map(() => 0);
  const sum = weights.reduce((totalWeight, weight) => totalWeight + (weight > 0n ? weight : 0n), 0n);
  if (sum === 0n) return weights.map(() => 0);
  const totalBi = BigInt(Math.trunc(total));
  const floors = weights.map((weight) => {
    const safe = weight > 0n ? weight : 0n;
    return (safe * totalBi) / sum;
  });
  let left = totalBi - floors.reduce((assigned, floor) => assigned + floor, 0n);
  const order = weights
    .map((weight, index) => ({ index, rem: ((weight > 0n ? weight : 0n) * totalBi) % sum }))
    .sort((left, right) => (left.rem === right.rem ? left.index - right.index : left.rem > right.rem ? -1 : 1));
  for (const item of order) {
    if (left <= 0n) break;
    floors[item.index] = (floors[item.index] ?? 0n) + 1n;
    left -= 1n;
  }
  return floors.map((value) => Number(value));
}

// ---------------------------------------------------------------------------
// Crew size: labor minutes vs time on site
// ---------------------------------------------------------------------------

/**
 * Wall-clock time on site: labor minutes split across the crew.
 *
 * A quote's minutes are labor minutes — the sum of every person's work, and
 * what the price is built from. How long the crew is actually at the property
 * is that labor divided by the people sent, and the two are the same number
 * only for a crew of one. This is deliberately NOT an engine input: crew size
 * must never reach a money column (labor is billed by its minutes whoever
 * they are split across), so it lives beside the engine rather than inside it,
 * and every surface that shows or schedules a duration derives it through this
 * one helper instead of doing its own division.
 *
 * Rounded up to the whole minute: a crew cannot leave mid-minute, and rounding
 * down would promise the customer a finish time the work cannot meet. A crew
 * size of zero, or a missing one, reads as one — the assumption every quote
 * made before the field existed.
 */
export function onSiteMinutes(laborMinutes: number, crewSize: number): number {
  const labor = Math.max(0, Math.trunc(laborMinutes));
  const crew = Math.max(1, Math.trunc(crewSize || 1));
  return Math.ceil(labor / crew);
}

// ---------------------------------------------------------------------------
// Formatting helpers (used by the UI and the printable quote)
// ---------------------------------------------------------------------------

export function formatCents(cents: number, currencyCode = DEFAULT_CURRENCY_CODE, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
  }).format(cents / 100);
}

/** "3 hr 15 min", "45 min", "2 hr". */
/**
 * Units come from a table rather than a translation bundle because both callers
 * are outside i18next's reach: the quote page and PDF are composed in the
 * Worker in the *company's* language, and the quote editor renders in whatever
 * language the browser chose. Abbreviations, deliberately — these sit inside
 * table cells and next to prices, where "horas" would wrap.
 */
const MINUTE_UNITS: Readonly<Record<SupportedLanguage, { hour: string; minute: string }>> = {
  en: { hour: 'hr', minute: 'min' },
  es: { hour: 'h', minute: 'min' },
};

export function formatMinutes(minutes: number, language: SupportedLanguage = DEFAULT_LANGUAGE): string {
  const safe = Math.max(0, Math.trunc(minutes));
  const hours = Math.floor(safe / 60);
  const remainder = safe % 60;
  const units = MINUTE_UNITS[language];
  if (hours === 0) return `${remainder} ${units.minute}`;
  if (remainder === 0) return `${hours} ${units.hour}`;
  return `${hours} ${units.hour} ${remainder} ${units.minute}`;
}

/** Decimal hours, for quotes that show labor time to the customer. */
export function minutesToHours(minutes: number): number {
  return Math.round((minutes / 60) * 100) / 100;
}
